import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { IPanel, Panel, PanelPropertyValues } from "./Panel";
import { NAMESPACE } from "./_Namespace";

import "./MessagePanel.scss";

export enum MessagePanelTypeEnum
{
    none = "none",
    negative = "negative",
    positive = "positive",
}

export type MessagePanelPropertyValues =
    PanelPropertyValues
    & {
        icon: string;
        title: string;
        type: string;
    };

export type MessagePanelProperties = {
    [name in keyof MessagePanelPropertyValues]: Property<MessagePanelPropertyValues[name]>
};

export interface IMessagePanel extends IPanel, MessagePanelProperties
{
}

export class MessagePanel extends Panel implements IMessagePanel
{
    public static override classNamespace = NAMESPACE + "MessagePanel";
    public static override className = "MessagePanel";

    // #region Global Variables

    protected _iconElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public icon!: PropertyString;
    public title!: PropertyString;
    public type!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<MessagePanelPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-message-panel ui icon message");

        // Trigger Property Changes
        this.icon.triggerChange();
        this.title.triggerChange();
        this.type.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _iconChanged(change: PropertyChange<string>)
    {
        if (!this._iconElementRef)
            return;

        const
            iconElement = this._iconElementRef.current.get(0);

        if (!iconElement)
            return;

        iconElement.className = StringHelper.empty;
        this._iconElementRef.current.detach();

        if (change.currentValue)
        {
            this._iconElementRef.current.addClass(StringHelper.format("icon {0}", change.currentValue));
            this._iconElementRef.current.prependTo(this.$$element.get());
        }
    }
    protected _titleChanged(change: PropertyChange<string>)
    {
        this._containerElementRef.current.find(">.header").text(change.currentValue);
    }
    protected _typeChanged(change: PropertyChange<string>)
    {
        this.removeCssClass("negative positive");

        switch (change.currentValue)
        {
            case MessagePanelTypeEnum.negative:
                this.addCssClass("negative");
                break;
            case MessagePanelTypeEnum.positive:
                this.addCssClass("positive");
                break;
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: MessagePanelPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._iconElementRef = new ElementRef();

        this.icon = new PropertyString(this, propertyValues.icon, this._iconChanged);
        this.title = new PropertyString(this, propertyValues.title, this._titleChanged);
        this.type = new PropertyString(this, propertyValues.type, this._typeChanged);
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <div ref={this._containerElementRef} class="content">
                    <div class="header"></div>
                    <p></p>
                    {children ?? this._createChildrenHtml()}
                </div>
            </div>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this._containerElementRef.current.find(">p").text(change.currentValue);
    }

    // #endregion
}